import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { PRIORITY_META, CONTROL_STATUS_META, FRAMEWORK_COLORS } from "../contexts/ComplianceContext";
import { ArrowLeft, ClipboardCheck, FileText } from "lucide-react";
import { cn } from "../lib/utils";

export default function FrameworkDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [fw, setFw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDomain, setSelectedDomain] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/compliance/frameworks/${id}`);
        setFw(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const filteredControls = useMemo(() => {
    if (!fw) return [];
    if (selectedDomain === "all") return fw.controls;
    return fw.controls.filter((c) =>
      (c.mappings || []).some(
        (m) => m.framework_id === fw.id && m.domain_id === selectedDomain
      )
    );
  }, [fw, selectedDomain]);

  const startAssessment = async () => {
    try {
      const { data } = await api.post("/compliance/assessments", {
        framework_id: fw.id,
        name: `${fw.name} Self-Assessment (${new Date().toLocaleDateString()})`,
        scope: "Organisation-wide self-assessment",
      });
      navigate(`/assessments/${data.id}`);
    } catch (e) {
      console.error("Failed to start assessment", e);
    }
  };

  if (loading) {
    return <div className="space-y-4"><div className="h-8 w-64 bg-muted rounded animate-pulse" /><div className="h-40 bg-muted rounded animate-pulse" /></div>;
  }
  if (!fw) {
    return <div className="text-muted-foreground">Framework not found.</div>;
  }
  const color = FRAMEWORK_COLORS[fw.code] || "#2563EB";
  const implemented = fw.controls.filter((c) => c.status === "implemented").length;
  const total = fw.controls.length;
  const completion = total ? Math.round((implemented / total) * 100) : 0;

  return (
    <div className="space-y-6">
      <Link
        to="/frameworks"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="w-4 h-4" /> All frameworks
      </Link>

      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div
              className="w-12 h-12 rounded-md flex items-center justify-center text-white"
              style={{ backgroundColor: color }}
            >
              <ClipboardCheck className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-semibold tracking-tight">{fw.name}</h1>
              <div className="text-sm text-muted-foreground">
                {fw.code} · v{fw.version || "—"}
              </div>
              {fw.description && <p className="text-sm text-muted-foreground mt-2">{fw.description}</p>}
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="text-3xl font-semibold tabular-nums" style={{ color }}>
                {completion}%
              </div>
              <div className="text-xs text-muted-foreground">
                {implemented} / {total} controls implemented
              </div>
              <Button data-testid="framework-start-assessment" onClick={startAssessment} size="sm">
                <FileText className="w-4 h-4" /> Start self-assessment
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Domains list */}
        <Card className="lg:col-span-1 h-fit">
          <CardHeader>
            <CardTitle>Domains</CardTitle>
            <CardDescription>Filter controls by domain.</CardDescription>
          </CardHeader>
          <CardContent className="p-2 pb-4 space-y-1">
            <button
              onClick={() => setSelectedDomain("all")}
              data-testid="domain-all"
              className={cn(
                "w-full flex items-center justify-between text-left px-3 py-2 rounded-md text-sm",
                selectedDomain === "all"
                  ? "bg-primary-subtle text-primary dark:bg-primary/15"
                  : "hover:bg-muted"
              )}
            >
              <span className="font-medium">All controls</span>
              <span className="text-xs tabular-nums">{fw.controls.length}</span>
            </button>
            {fw.domains.map((d) => {
              const count = fw.controls.filter((c) =>
                (c.mappings || []).some((m) => m.framework_id === fw.id && m.domain_id === d.id)
              ).length;
              return (
                <button
                  key={d.id}
                  onClick={() => setSelectedDomain(d.id)}
                  data-testid={`domain-${d.code}`}
                  className={cn(
                    "w-full flex items-center justify-between text-left px-3 py-2 rounded-md text-sm",
                    selectedDomain === d.id
                      ? "bg-primary-subtle text-primary dark:bg-primary/15"
                      : "hover:bg-muted"
                  )}
                >
                  <span className="min-w-0 truncate">
                    <span className="font-mono text-xs mr-2 text-muted-foreground">{d.code}</span>
                    {d.name}
                  </span>
                  <span className="text-xs tabular-nums shrink-0 ml-2">{count}</span>
                </button>
              );
            })}
          </CardContent>
        </Card>

        {/* Controls table */}
        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle>Controls</CardTitle>
            <CardDescription>
              {filteredControls.length} controls
              {selectedDomain !== "all" && ` in this domain`}
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/40">
                  <tr className="border-t border-b border-border">
                    <th className="text-left py-2.5 px-4 label-eyebrow">Reference</th>
                    <th className="text-left py-2.5 px-4 label-eyebrow">Control</th>
                    <th className="text-left py-2.5 px-4 label-eyebrow">Priority</th>
                    <th className="text-left py-2.5 px-4 label-eyebrow">Status</th>
                    <th className="text-left py-2.5 px-4 label-eyebrow">Frameworks</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredControls.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-muted-foreground">
                        No controls in this domain.
                      </td>
                    </tr>
                  )}
                  {filteredControls.map((c) => {
                    const ref =
                      (c.mappings || []).find((m) => m.framework_id === fw.id)?.control_ref || "—";
                    const s = CONTROL_STATUS_META[c.status] || { label: c.status, variant: "neutral" };
                    const p = PRIORITY_META[c.priority] || { label: c.priority, variant: "neutral" };
                    return (
                      <tr
                        key={c.id}
                        data-testid={`framework-control-${c.id}`}
                        className="border-b border-border hover:bg-muted/40 transition-colors cursor-pointer"
                        onClick={() => navigate(`/controls/${c.id}`)}
                      >
                        <td className="py-3 px-4 font-mono text-xs whitespace-nowrap">{ref}</td>
                        <td className="py-3 px-4 font-medium">{c.title}</td>
                        <td className="py-3 px-4">
                          <Badge variant={p.variant}>{p.label}</Badge>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant={s.variant}>{s.label}</Badge>
                        </td>
                        <td className="py-3 px-4">
                          <span className="text-xs text-muted-foreground">
                            {(c.mappings || []).length} framework{(c.mappings || []).length !== 1 ? "s" : ""}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
